import React, { useEffect, useState } from "react";
import { api, formatApiError, rupiah, fileUrl } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GraduationCap, UserPlus, Pencil, Eye, EyeOff, ImagePlus, User, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", nisn: "", parent_id: "", gender: "putra", pin: "", angkatan: new Date().getFullYear(), monthly_fee: "" };

export default function DataSantri() {
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shownPin, setShownPin] = useState({});
  const [fAngkatan, setFAngkatan] = useState("all");
  const [fGender, setFGender] = useState("all");
  const [query, setQuery] = useState("");

  const load = () => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/admin/parents").then((r) => setParents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setPhoto(null); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, nisn: s.nisn, parent_id: s.parent_id, gender: s.gender, pin: s.pin,
              angkatan: s.angkatan || new Date().getFullYear(), monthly_fee: s.monthly_fee || "" });
    setPhoto(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !form.parent_id) return toast.error("Pilih orang tua");
    setSaving(true);
    try {
      let sid = editing?.id;
      const payload = {
        name: form.name, nisn: form.nisn, gender: form.gender, pin: form.pin,
        angkatan: parseInt(form.angkatan), monthly_fee: parseFloat(form.monthly_fee || 0),
      };
      if (editing) {
        await api.put(`/admin/students/${editing.id}`, { ...payload, parent_id: form.parent_id });
      } else {
        const { data } = await api.post("/admin/students", { ...payload, parent_id: form.parent_id });
        sid = data.id;
      }
      if (photo && sid) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/admin/students/${sid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing ? "Data santri diperbarui" : "Santri ditambahkan");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const angkatanList = [...new Set(students.map((s) => s.angkatan).filter(Boolean))].sort();

  const del = async (s) => {
    if (!window.confirm(`Hapus santri "${s.name}"? Tagihan & riwayat saldo santri ini akan ikut terhapus.`)) return;
    try {
      await api.delete(`/admin/students/${s.id}`);
      toast.success(`Santri "${s.name}" dihapus`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const filtered = students.filter((s) =>
    (fAngkatan === "all" || s.angkatan === parseInt(fAngkatan)) &&
    (fGender === "all" || s.gender === fGender) &&
    (s.name.toLowerCase().includes(query.toLowerCase()) || (s.nisn || "").includes(query))
  );
  const pg = usePaged(filtered, 10);

  return (
    <div>
      <PageHeader
        title="Data Santri"
        subtitle="Dikelompokkan per Angkatan & Jenis Kelamin. PIN dipakai untuk transaksi Kopontren."
        action={
          <Button onClick={openAdd} data-testid="add-student-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
            <UserPlus size={18} className="mr-2" /> Tambah Santri
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={fAngkatan} onValueChange={setFAngkatan}>
          <SelectTrigger data-testid="filter-angkatan" className="w-44 rounded-xl bg-white"><SelectValue placeholder="Angkatan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Angkatan</SelectItem>
            {angkatanList.map((a) => <SelectItem key={a} value={String(a)}>Angkatan {a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fGender} onValueChange={setFGender}>
          <SelectTrigger data-testid="filter-gender" className="w-40 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="putra">Putra</SelectItem>
            <SelectItem value="putri">Putri</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="student-search" placeholder="Cari nama / NISN…" className="pl-10 rounded-xl bg-white" />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <Empty><GraduationCap className="mx-auto mb-2" /> Belum ada santri.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Foto</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Angkatan</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead className="text-right">Biaya Bulanan</TableHead>
                <TableHead>Orang Tua</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((s) => (
                <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                  <TableCell>
                    {s.photo_path ? (
                      <img src={fileUrl(s.photo_path)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-stone-400"><User size={18} /></div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-stone-800">{s.name}</TableCell>
                  <TableCell className="font-mono text-sm">{s.nisn}</TableCell>
                  <TableCell>{s.angkatan}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.gender === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{s.gender}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-sm" data-testid={`pin-${s.id}`}>{shownPin[s.id] ? s.pin : "••••"}</span>
                      <button onClick={() => setShownPin((p) => ({ ...p, [s.id]: !p[s.id] }))} data-testid={`toggle-pin-${s.id}`} className="text-stone-400 hover:text-emerald-600">
                        {shownPin[s.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(s.monthly_fee || 0)}</TableCell>
                  <TableCell className="text-sm text-stone-600">{s.parent_name}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} className="rounded-lg">
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(s)} data-testid={`del-student-${s.id}`} className="ml-1 text-rose-600">
                      <Trash2 size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Santri" : "Tambah Santri"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="flex items-center gap-4">
              <label htmlFor="photo" className="cursor-pointer">
                {photo ? (
                  <img src={URL.createObjectURL(photo)} alt="" className="w-20 h-20 rounded-xl object-cover border" />
                ) : editing?.photo_path ? (
                  <img src={fileUrl(editing.photo_path)} alt="" className="w-20 h-20 rounded-xl object-cover border" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-stone-100 border-2 border-dashed flex items-center justify-center text-stone-400"><ImagePlus size={22} /></div>
                )}
                <input id="photo" type="file" accept="image/*" data-testid="student-photo-input" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} />
              </label>
              <div className="text-sm text-stone-500">Pas Foto Santri<br /><span className="text-xs text-stone-400">Klik gambar untuk unggah</span></div>
            </div>
            <div className="space-y-2">
              <Label>Nama Santri</Label>
              <Input data-testid="student-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>NISN</Label>
                <Input data-testid="student-nisn-input" value={form.nisn} onChange={(e) => setForm({ ...form, nisn: e.target.value })} required className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>PIN Transaksi</Label>
                <Input data-testid="student-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} required className="rounded-xl" placeholder="4-6 digit" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Angkatan (Tahun Masuk)</Label>
                <Input data-testid="student-angkatan-input" type="number" value={form.angkatan} onChange={(e) => setForm({ ...form, angkatan: e.target.value })} required className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Biaya Bulanan (Rp)</Label>
                <Input data-testid="student-fee-input" type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} className="rounded-xl" placeholder="cth 350000" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Jenis Kelamin / Unit</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="student-gender-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="putra">Putra</SelectItem>
                  <SelectItem value="putri">Putri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Orang Tua {editing && <span className="text-xs text-stone-400">(bisa diganti bila salah pilih)</span>}</Label>
              <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                <SelectTrigger data-testid="student-parent-select" className="rounded-xl"><SelectValue placeholder="Pilih orang tua" /></SelectTrigger>
                <SelectContent>
                  {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-student-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                {saving ? "Menyimpan…" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
